import { Controller } from "@hotwired/stimulus"

// A stack on a desktop home (assistants/_home_desktop): several dials in one
// card, taking turns like a Smart Stack on a Mac. Every `every` seconds the
// current dial slides up and out and the next one rises into place. It holds
// still while the pointer is on it, while a menu is open, in edit mode and while
// the tab is hidden; the dots on the right edge (on hover) and the scroll wheel
// move it by hand.
//
// Each dial is its own lazy turbo-frame, so its data is as fresh as its own
// cache. A dial that comes round again after REFRESH_AFTER is reloaded as it
// shows, so a market price on a stack left open all day keeps moving.
const REFRESH_AFTER = 60_000
const WHEEL_PAUSE = 500

export default class extends Controller {
    static targets = ["layer", "dot"]
    static values = { every: { type: Number, default: 10 } }

    connect() {
        this.index = 0
        this._loadedAt = new Map()
        this._onLoad = (event) => this._loadedAt.set(event.target, Date.now())
        this.element.addEventListener("turbo:frame-load", this._onLoad)
        this._onEnter = () => { this._hovering = true }
        this._onLeave = () => { this._hovering = false }
        this.element.addEventListener("pointerenter", this._onEnter)
        this.element.addEventListener("pointerleave", this._onLeave)
        this._onWheel = (event) => this._wheel(event)
        this.element.addEventListener("wheel", this._onWheel, { passive: false })
        this._reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
        this._start()
    }

    disconnect() {
        clearInterval(this._timer)
        this.element.removeEventListener("turbo:frame-load", this._onLoad)
        this.element.removeEventListener("pointerenter", this._onEnter)
        this.element.removeEventListener("pointerleave", this._onLeave)
        this.element.removeEventListener("wheel", this._onWheel)
    }

    everyValueChanged() {
        if (this._timer !== undefined) this._start()
    }

    // A dot: straight to that dial.
    show(event) {
        event.preventDefault()
        event.stopPropagation()
        this._go(Number(event.params.index), Number(event.params.index) >= this.index ? 1 : -1)
        this._start()
    }

    _start() {
        clearInterval(this._timer)
        this._timer = setInterval(() => this._tick(), Math.max(this.everyValue, 3) * 1000)
    }

    _tick() {
        if (this._held()) return
        this._go(this.index + 1, 1)
    }

    _held() {
        return this._hovering || document.hidden ||
            this.element.classList.contains("has-menu") ||
            this.element.closest(".is-editing") !== null
    }

    _wheel(event) {
        if (Math.abs(event.deltaY) < 8 || this.layerTargets.length < 2) return
        event.preventDefault()
        const now = Date.now()
        if (now - (this._wheelAt || 0) < WHEEL_PAUSE) return
        this._wheelAt = now
        this._go(this.index + (event.deltaY > 0 ? 1 : -1), event.deltaY > 0 ? 1 : -1)
        this._start()
    }

    // Slide from the current dial to `next`: the leaving one goes up (or down,
    // going back) and the arriving one comes from the other side.
    _go(next, direction) {
        const count = this.layerTargets.length
        if (count < 2) return
        next = ((next % count) + count) % count
        if (next === this.index) return

        const leaving = this.layerTargets[this.index]
        const arriving = this.layerTargets[next]
        this.index = next

        this._refresh(arriving)
        if (this._reducedMotion) {
            leaving.classList.remove("is-current")
            arriving.classList.add("is-current")
        } else {
            arriving.classList.remove("is-current", "is-leaving", "from-above", "from-below")
            arriving.classList.add(direction > 0 ? "from-below" : "from-above")
            void arriving.offsetHeight
            arriving.classList.remove("from-below", "from-above")
            arriving.classList.add("is-current")
            leaving.classList.remove("is-current")
            leaving.classList.add(direction > 0 ? "is-leaving" : "is-leaving-down")
            setTimeout(() => leaving.classList.remove("is-leaving", "is-leaving-down"), 600)
        }
        this.layerTargets.forEach((layer, i) => layer.setAttribute("aria-hidden", String(i !== next)))
        this.dotTargets.forEach((dot, i) => dot.classList.toggle("is-current", i === next))
    }

    _refresh(layer) {
        const frame = layer.querySelector("turbo-frame[src]")
        const loadedAt = frame && this._loadedAt.get(frame)
        if (loadedAt && Date.now() - loadedAt > REFRESH_AFTER) frame.reload()
    }
}
